'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'
import { featureGradients } from './marketplace-tokens'
import type { FeatureFixture } from './marketplace-data'

export function FeatureCard({
  feature,
  onOpenChannel,
}: {
  feature: FeatureFixture
  onOpenChannel: (channelKey: string) => void
}) {
  const palette = featureGradients[feature.gradientKey]
  const light = palette.textTone === 'light'
  const ink = palette.ink ?? '#FFFFFF'
  const meta = palette.meta ?? 'rgba(255,255,255,0.72)'
  const bodyColor = light ? 'rgba(255,255,255,0.9)' : meta
  const kickerColor = light ? 'rgba(255,255,255,0.85)' : meta

  return (
    <button
      type="button"
      onClick={() => onOpenChannel(feature.channelKey)}
      style={{ background: palette.gradient }}
      className="group relative flex h-[288px] w-[480px] flex-none flex-col justify-between overflow-hidden rounded-[18px] p-[30px] text-left transition-transform duration-200 ease-out [scroll-snap-align:start] hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
    >
      {palette.decos?.map((d, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute rounded-full"
          style={{
            top: d.top,
            right: d.right,
            bottom: d.bottom,
            left: d.left,
            width: d.size,
            height: d.size,
            background: d.color,
          }}
        />
      ))}

      <div className="relative z-[2]">
        <div className="mb-4 flex items-center gap-2">
          <span
            className={cn(
              'rounded-[5px] px-2.5 py-[5px] font-mono text-[11px] font-medium uppercase tracking-[0.1em]',
              light
                ? 'bg-white/20 text-white'
                : 'bg-black/10 text-[#5C4400]',
            )}
          >
            {feature.tag}
          </span>
          <span
            className="font-mono text-xs uppercase tracking-[0.08em]"
            style={{ color: kickerColor }}
          >
            {feature.kicker}
          </span>
        </div>
        <h3
          className="mb-3 text-[38px] font-bold leading-[1.04] tracking-[-0.025em]"
          style={{ color: ink }}
        >
          {feature.titleLines[0]}
          <br />
          {feature.titleLines[1]}
        </h3>
        <p
          className="m-0 max-w-[350px] text-sm leading-[1.5]"
          style={{ color: bodyColor }}
        >
          {feature.body}
        </p>
      </div>

      <div className="relative z-[2] flex items-center justify-between">
        {feature.logos ? (
          <LogoStack logos={feature.logos} accent={feature.gradientKey === 'streaming' ? '#00D67E' : undefined} />
        ) : feature.meta ? (
          <span
            className="font-mono text-xs"
            style={{ color: meta }}
          >
            {feature.meta}
          </span>
        ) : (
          <span />
        )}
        <span className="inline-flex items-center gap-1.5 rounded-[9px] bg-[#1A1A1A] px-4 py-2.5 text-[13px] font-semibold text-white">
          Install
          <HugeiconsIcon icon={ArrowRight01Icon} className="size-3.5" aria-hidden />
        </span>
      </div>
    </button>
  )
}

function LogoStack({ logos, accent }: { logos: string[]; accent?: string }) {
  return (
    <div className="flex">
      {logos.map((label, i) => {
        const isFirst = i === 0
        const isLast = i === logos.length - 1
        const isWhite = isLast && logos.length > 1
        return (
          <span
            key={`${label}-${i}`}
            className={cn(
              'grid size-8 place-items-center rounded-lg border-[1.5px] border-white/25 text-xs font-semibold',
              i > 0 && '-ml-2.5',
              isWhite ? 'bg-white text-[#1A1A1A]' : 'bg-[#0F0F0E] text-white',
            )}
            style={isFirst && accent ? { color: accent } : undefined}
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}
